
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, UserCircle, Bookmark, BookOpen, Star, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Progress } from "@/components/ui/progress";

interface FeedbackItem {
  id: string;
  course_id?: string;
  user: {
    name: string;
    position: string;
    school: string;
  };
  course: {
    title: string;
    category: string;
    id?: string;
  };
  rating: number;
  feedback: string;
  date: string;
  recommendRating?: number;
}

interface FeedbackDetailCardProps {
  feedback: FeedbackItem;
  clickable?: boolean;
}

const FeedbackDetailCard: React.FC<FeedbackDetailCardProps> = ({ feedback, clickable = true }) => {
  const navigate = useNavigate();
  
  const handleCardClick = () => {
    if (clickable && (feedback.course.id || feedback.course_id)) {
      const courseId = feedback.course.id || feedback.course_id;
      console.log("Navigating to course feedback:", courseId);
      navigate(`/admin/feedback/course/${courseId}`);
    }
  };
  
  return (
    <Card 
      className={`bg-white rounded-lg hover:shadow-md transition-all duration-300 ${clickable ? 'cursor-pointer hover:-translate-y-1 border-accent-light' : 'border-accent-subtle'}`}
      onClick={handleCardClick}
    >
      <CardContent className="p-6 space-y-4">
        <div className="flex items-start justify-between">
          <div className="space-y-2 flex-1">
            <h3 className="font-title text-xl text-primary font-bold leading-tight">
              {feedback.course.title}
            </h3>
            
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-accent" />
              <span className="text-sm font-body text-gray-600">
                {new Date(feedback.date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </span>
            </div>
            
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-accent" />
                <Badge variant="outline" className="font-body text-xs bg-primary/5 text-primary border-accent-light">
                  {feedback.course.category}
                </Badge>
              </div>
              
              <div className="flex items-center">
                {[...Array(4)].map((_, i) => (
                  <Star 
                    key={i} 
                    className={`h-4 w-4 ${i < feedback.rating ? 'text-amber-400 fill-amber-400' : 'text-accent-light'}`}
                  />
                ))}
              </div>
            </div>
          </div>
          
          {clickable && <ChevronRight className="h-5 w-5 text-accent" />}
        </div>
        
        <Separator className="my-4 bg-accent-subtle" />
        
        <div>
          <div className="flex items-center mb-2">
            <UserCircle className="h-5 w-5 mr-2 text-primary" />
            <h4 className="font-section font-medium text-sm text-primary">Participant</h4>
          </div>
          <div className="text-sm pl-7">
            <p className="font-body">{feedback.user.name}</p>
            <p className="text-muted-foreground font-body">{feedback.user.position} · {feedback.user.school}</p>
          </div>
        </div>
        
        <div>
          <div className="flex items-center mb-2">
            <BookOpen className="h-5 w-5 mr-2 text-primary" />
            <h4 className="font-section font-medium text-sm text-primary">Feedback</h4>
          </div>
          <p className="text-sm pl-7 font-body">{feedback.feedback}</p>
        </div>
        
        {feedback.recommendRating !== undefined && (
          <div>
            <div className="flex items-center mb-2">
              <Bookmark className="h-5 w-5 mr-2 text-primary" />
              <h4 className="font-section font-medium text-sm text-primary">Would Recommend</h4>
            </div>
            <div className="pl-7 flex items-center gap-2">
              <Progress 
                value={(feedback.recommendRating / 4 * 100)} 
                className="w-full max-w-xs h-2.5" 
                indicatorClassName="bg-accent"
              />
              <span className="text-sm font-body">{(feedback.recommendRating / 4 * 100).toFixed(0)}%</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FeedbackDetailCard;
